import './styles.scss';

import { ImportTypeEnum, StagedTrip, StagedTripIngestActionEnum } from '@/models/gen/graphql';
import ManifestPreviewTableHeader, { useManifestPreviewTable } from './ManifestPreviewTableHeader';
import { getClasses, getProperty } from '@/utils';

import EditFlagsModal from '@/pages/Manifests/EditFlagsModal';
import { INVALID_DATE_STRING } from '@/constants';
import ManifestPreviewTableRow from './ManifestPreviewTableRow';
import Table from '@/common/Table';
import { useManifestState } from '../store';
import { useMemo } from 'react';

export type ManifestPreviewTableProps = {
  data: StagedTrip[];
  previewType: ImportTypeEnum;
  selected: string[];
  onEdit: (row: StagedTrip) => (field: string) => (val: string) => void;
  onSelect: (id: string | any[]) => void;
};

const ManifestPreviewTable = ({ data, previewType, selected, onEdit, onSelect }: ManifestPreviewTableProps): JSX.Element => {
  const [{ invalidStagedTripsIds }] = useManifestState(({ state }) => [
    { invalidStagedTripsIds: Object.keys(state.manifestPreview.invalidStagedTrips) },
  ]);
  const [{ sortBy, sortDirection }, setState] = useManifestPreviewTable(({ state, setState }) => [
    {
      sortBy: state.sorting.column,
      sortDirection: state.sorting.direction,
    },
    setState,
  ]);
  const isSelectable = (trip: StagedTrip): boolean =>
    trip.ingestAction !== StagedTripIngestActionEnum.Exclude && trip.ingestAction !== StagedTripIngestActionEnum.Not;

  const sortedRows = useMemo(() => {
    return [...data].sort((a, b) => {
      if (invalidStagedTripsIds.includes(a.id) && invalidStagedTripsIds.includes(b.id)) return 0;
      if (invalidStagedTripsIds.includes(a.id)) return -1;
      if (!sortBy || !sortDirection) return 0;
      let aValue: string | number = getProperty(sortBy, a) as string | number;
      let bValue: string | number = getProperty(sortBy, b) as string | number;

      // Special handling for scheduled field to treat INVALID_DATE_STRING as undefined
      if (sortBy === 'scheduled') {
        aValue = aValue === INVALID_DATE_STRING ? '' : aValue;
        bValue = bValue === INVALID_DATE_STRING ? '' : bValue;
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortBy, sortDirection]);

  return (
    <>
      <Table
        className={getClasses('ManifestPreviewTable', previewType === ImportTypeEnum.Addon ? 'AddonPreview' : 'ManifestPreview')}
        header={
          <ManifestPreviewTableHeader
            previewType={previewType}
            selected={selected}
            onSelect={onSelect}
            isSelectable={isSelectable}
            data={data}
          />
        }
        showControls
      >
        {sortedRows.map(
          (row): JSX.Element => (
            <ManifestPreviewTableRow
              classes={getClasses(selected.includes(row?.id) ? 'selected' : '', row?.ingestAction)}
              row={row}
              key={row?.id}
              previewType={previewType}
              onSelect={onSelect}
              onEdit={onEdit}
              isSelectable={isSelectable}
              isSelected={selected.includes(row?.id)}
            />
          )
        )}
      </Table>
      <EditFlagsModal />
    </>
  );
};

export default ManifestPreviewTable;
